import { Camera, Object3D, Raycaster, Scene, Vector2, type Intersection, type WebGLRenderer } from 'three'
import { ThreeItemKeys, ThreeItemTypes } from '../constants/three'

export const getRaycasterIntersects = (
  renderer: WebGLRenderer,
  e: MouseEvent,
  scene: Scene,
  camera: Camera,
  roots: Object3D[] = scene.children,
) => {
  if (!renderer.domElement.parentElement?.contains(e.target as Node)) {
    return []
  }

  const raycaster = new Raycaster()
  // Without this threshold, the selection of lines can have an offset on z and be less precise
  raycaster.params.Line.threshold = 0.1
  const canvasPosition = renderer.domElement.getBoundingClientRect()
  const mouseX = e.clientX - canvasPosition.left
  const mouseY = e.clientY - canvasPosition.top
  const pointer = new Vector2(
    2 * (mouseX / renderer.domElement.clientWidth) - 1,
    -(2 * (mouseY / renderer.domElement.clientHeight) - 1),
  )

  raycaster.setFromCamera(pointer, camera)

  return raycaster.intersectObjects(roots, true)
}

type GetRaycasterElementOptions = {
  ignoreLockedParents: boolean
  intersectableItems: (typeof ThreeItemTypes[keyof typeof ThreeItemTypes])[]
  // Restricts the raycast to these objects' subtrees instead of the whole scene -
  // use this to make placement unambiguous when other objects overlap on screen.
  roots: Object3D[]
}

export const getRaycasterElement = (
  renderer: WebGLRenderer,
  e: MouseEvent,
  scene: Scene,
  camera: Camera,
  options: Partial<GetRaycasterElementOptions> = {},
): { intersection: Intersection, element: Object3D } | null => {
  const intersectableItems = options.intersectableItems || [
    ThreeItemTypes.FILE,
    ThreeItemTypes.GROUP,
    ThreeItemTypes.PRINTER,
    ThreeItemTypes.FORBIDDEN_ZONE,
  ]

  const intersects = getRaycasterIntersects(renderer, e, scene, camera, options.roots)
  for (const intersect of intersects) {
    if (
      intersect.object.visible
      && intersectableItems.includes(
        intersect.object.userData[ThreeItemKeys.ITEM_TYPE],
      )
    ) {
      return { intersection: intersect, element: intersect.object }
    }

    let foundObject: Object3D | undefined
    intersect.object.traverseAncestors((object) => {
      if (
        object.visible
        && intersectableItems.includes(
          object.userData[ThreeItemKeys.ITEM_TYPE],
        )
      ) {
        if (!foundObject) {
          foundObject = object
        }
      }
    })

    if (foundObject) {
      if (foundObject?.parent?.userData?.[ThreeItemKeys.LOCKED] && !options.ignoreLockedParents) {
        return { intersection: intersect, element: foundObject.parent }
      }

      return { intersection: intersect, element: foundObject }
    }
  }

  return null
}
